import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api.js';
import RecordForm from '../../components/RecordForm/RecordForm.jsx';
import { actionIcon } from '../../lib/icons.jsx';
import { useTableProfile } from '../../hooks/useTableProfile.js';
import { DataTable } from '@shared/UI/DataTable';
import '@shared/UI/DataTable/DataTable.css';
import './ObjectWorkspace.css';

const OBJECTS_WITH_BOARD = ['email_order', 'opportunity'];

const parseData = (rec) => { try { return typeof rec.data === 'string' ? JSON.parse(rec.data) : (rec.data || {}); } catch (_) { return {}; } };
const fieldValue = (rec, f) => (f.is_primary_label ? rec.name : parseData(rec)[f.name]);
const fmtVal = (v, f) => {
  if (v === null || v === undefined || v === '') return '—';
  if (f.field_type === 'currency') return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v);
  if (f.field_type === 'percent') return v + '%';
  if (f.field_type === 'select') { try { const o = JSON.parse(f.options_json || '[]').find((x) => x.value === v); return o ? o.label : v; } catch (_) { return v; } }
  return String(v);
};

const ObjectWorkspace = ({ object }) => {
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { layoutConfig, saveLayout } = useTableProfile('gencrm_workspace');

  const load = async (q = '') => {
    setLoading(true); setError(null);
    try {
      const [fR, rR] = await Promise.all([
        api.get(`/objects/${object.id}/fields`),
        api.get(`/${object.id}/records`, { params: { search: q || undefined, pageSize: 100 } })
      ]);
      setFields(fR.data || []);
      setRecords((rR.data && rR.data.records) || []);
      setTotal((rR.data && rR.data.total) || 0);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [object.id]);

  const cols = useMemo(() => fields.filter((f) => f.is_searchable || f.is_primary_label).slice(0, 6), [fields]);
  const boardable = OBJECTS_WITH_BOARD.includes(object.name_singular);

  const onNew = () => { setEditing(null); setFormOpen(true); };
  const onEdit = (rec) => { setEditing(rec); setFormOpen(true); };
  const onSaved = () => { setFormOpen(false); setEditing(null); load(search); };
  const onDelete = async (rec) => { if (confirm('Eliminar este registo?')) { await api.delete(`/byid/${rec.id}`); load(search); } };

  const columns = useMemo(() => {
    const out = [{ key: 'record_number', label: '#', width: 70, render: (r) => r.record_number || '' }];
    cols.forEach((f) => {
      out.push({ key: 'f_' + f.id, label: f.label, width: 180, cellClassName: f.is_primary_label ? 'gencrm_ws_primary' : undefined, render: (r) => fmtVal(fieldValue(r, f), f) });
    });
    out.push({ key: 'acoes', label: '', width: 90, hideable: false, searchable: false, cellClassName: 'gencrm_ws_actions', render: (r) => (
      <>
        <button className="gencrm_ws_iconbtn" title="Editar" onClick={(e) => { e.stopPropagation(); onEdit(r); }}>{actionIcon('edit', 16)}</button>
        <button className="gencrm_ws_iconbtn" title="Eliminar" onClick={(e) => { e.stopPropagation(); onDelete(r); }}>{actionIcon('delete', 16)}</button>
      </>
    ) });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cols]);

  return (
    <>
      <div className="gencrm_app__page-filters">
        <div className="gencrm_app__page-filters-left">
          <input className="gencrm_app__filter-input" placeholder="Pesquisar..." value={search}
            onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load(search)} />
        </div>
        <div className="gencrm_app__page-filters-right">
          <span className="gencrm_ws_count">{total} registos</span>
          {boardable && <button className="gencrm_btn gencrm_btn--secondary" onClick={() => navigate(`/b/${object.id}`)}>Board</button>}
          <button className="gencrm_btn" onClick={onNew}>+ Novo</button>
        </div>
      </div>

      {error && <div className="gencrm_ws_error">Erro: {error}</div>}

      <DataTable
        columns={columns}
        data={records}
        rowKey={(r) => r.id}
        rowClassName={() => 'gencrm_ws_row'}
        onRowClick={(r) => navigate(`/r/${r.id}`)}
        loading={loading}
        pageSize={50}
        density="compact"
        emptyText={'Sem registos. Clique em "+ Novo".'}
        exportPrefix="gencrm_workspace"
        layoutConfig={layoutConfig}
        onLayoutChange={saveLayout}
      />

      {formOpen && (
        <RecordForm objectId={object.id} fields={fields} record={editing}
          onSaved={onSaved} onClose={() => { setFormOpen(false); setEditing(null); }} />
      )}
    </>
  );
};

export default ObjectWorkspace;
